"use client"

import { HYPHEN_REGEX, PHONE_9DIGITS_REGEX, PHONE_REGEX } from "@constants/common";
import moment from "moment";

//check string is null or empty
export const isNullOrEmpty = (str: string) => {
    return !str || str.trim() === '';
}

//check string is not a email format
export const isNotEmail = (str: string) => {
    return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
}

//check string is a email format
export const isEmail = (str: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
}

export function a11yProps(name: string, index: number) {
    return {
        id: `${name}-tab-${index}`,
        'aria-controls': `${name}-tabpanel-${index}`,
        sx: {
            p: "0px 4.5%",
            "&.Mui-selected": {
                color: '#49454F',
                backgroundColor: "#FEF7FF",
                fontWeight: '900',
                borderColor: "#000 !important"
            },
        }
    };
}

export function childProps(name: string, index: number) {
    return {
        id: `${name}-tab-${index}`,
        'aria-controls': `${name}-tabpanel-${index}`,
        sx: {
            padding: "10px 3%",
            color: '#49454F',
            fontWeight: '400',
            "&.Mui-selected": {
                color: '#49454F',
                fontWeight: '900',
            },
        }
    };
}

export const redirectToHome = () => {
    if (typeof window !== 'undefined' && location) {
        location.href = "/"
    }
};

export function isUniqueStringArray(arr: string[]): boolean {
    return new Set(arr).size === arr.length;
}

export const IfBlankReturnNull = (str: string | null | undefined) => {
    return str?.trim().length === 0 ? null : str;
}

export function trimToMaxLength(input: string, maxLength: number) {
    // Check if the input length exceeds the maximum length
    if (input && input.length > maxLength) {
        // Remove the beginning characters to fit the max length
        return input.slice(input.length - maxLength);
    }
    return input;
}

export function stopAtMaxLength(input: string, maxLength: number) {
    // Check if the input length exceeds the maximum length
    if (input && input.length > maxLength) {
        // Prevent the next character from being entered.
        return input.slice(0, maxLength);
    }
    return input;
}

export const format10DigitsDashPhone = (input: string) => {
    if (input) {
        // Remove any non-digit characters
        input = input.replace(/\D/g, '');
        // Format the string as 000-000-0000
        if (input.length > 3 && input.length <= 6) {
            return input.replace(/(\d{3})/, '$1-');
        }
        if (input.length > 6 && input.length < 10) {
            return input.replace(/(\d{3})(\d{3})/, '$1-$2-');
        }
        input = trimToMaxLength(input, 10)
        return input.replace(PHONE_REGEX, '$1-$2-$3');
    }
    return "";
}

export const format10DigitsPhone = (input: string) => {
    if (input) {
        // Remove any non-digit characters
        input = input.replace(/\D/g, '');
        // Format the string as <= 10 digits, 0000000000 (no dashes)
        return stopAtMaxLength(input, 10); // trimToMaxLength(input, 10)
    }
    return "";
}

export const format9DigitsPhone = (input: string) => {
    if (input) {
        // Remove any non-digit characters
        input = input.replace(/\D/g, '');
        // Format the string as 000-000-000
        if (input.length > 3 && input.length <= 6) {
            return input.replace(/(\d{3})/, '$1-');
        }
        if (input.length > 6 && input.length < 9) {
            return input.replace(/(\d{3})(\d{3})/, '$1-$2-');
        }
        input = trimToMaxLength(input, 9)
        return input.replace(PHONE_9DIGITS_REGEX, '$1-$2-$3');
    }
    return "";
}

export const formatMonth = (input: string) => {
    if (input) {
        input = input.replace(/\D/g, '');

        // Format the string as MM/YY
        if (input.length > 4) {
            input = input.slice(0, 4);
        }

        if (input.length <= 2) {
            return input;
        } else {
            return input.replace(/(\d{2})(\d{2})/, '$1/$2');
        }
    }
    return "";
}

export const formatStringInputHyphenNN_NN = (input: string) => {
    if (input) {
        // Remove any non-digit characters
        input = input.replace(/\D/g, '');
        // Format the string as 00-00
        if (input.length > 2 && input.length <= 4) {
            return input.replace(/(\d{2})/, '$1-');
        }
        input = trimToMaxLength(input, 4)
        return input.replace(HYPHEN_REGEX, '$1-$2');
    }
    return "";
}

export const formatStringNumberCharOnly = (input: string, maxLength?: number) => {
    if (input) {
        // Remove any non-digit characters
        input = input.replace(/\D/g, '');
        if (maxLength) {
            input = trimToMaxLength(input, maxLength);
        }
        return input;
    }
    return "";
}

export const formatStringNumberCharOnlyWithLeadingZeros = (input: string, maxLength?: number) => {
    if (input) {
        // Remove any non-digit characters
        input = input.replace(/\D/g, '');
        if (maxLength) {
            input = input.padStart(maxLength, '0');
            input = trimToMaxLength(input, maxLength);
        }
        return input;
    }
    return "";
}

export function timeDifferenceString(date1: moment.Moment, date2: moment.Moment): string {
    const diffInMinutes = date2.diff(date1, 'minutes');
    let result = '';
    if (diffInMinutes < 1) {
        return 'now';
    }
    if (diffInMinutes < 60) {
        // Show minutes if less than 1 hour
        result = `${diffInMinutes}m`;
    } else if (diffInMinutes < 1440) { // 1440 minutes = 24 hours
        // Show hours if less than 24 hours
        const diffInHours = date2.diff(date1, 'hours');
        result = `${diffInHours}hr`;
    } else if (diffInMinutes < 10080) { // 10080 minutes = 7 days
        // Show days if less than 7 days
        const diffInDays = date2.diff(date1, 'days');
        result = `${diffInDays}d`;
    } else {
        // Show weeks if 7 days or more
        const diffInWeeks = date2.diff(date1, 'weeks');
        result = `${diffInWeeks}wk`;
    }

    return result;
}

export function getGridRowIdentifier(id?: string, index?: string) {
    return `${id}-${index}`;
}
